<script setup lang="ts">
/**
 * Sign-up form.
 *
 * FE-014 — rebuilt on the redesign primitives. Validation rules, the sign-up
 * call and the toasts are untouched; the PrimeVue fields with their inline
 * `:pt` overrides became primitives that already carry the tokens.
 */
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import { useAuthStore } from "@/features/auth/store";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { FairButton } from "@/ui/primitives/FairButton";
import { FairCheckbox } from "@/ui/primitives/FairCheckbox";
import { FairIcon } from "@/ui/primitives/FairIcon";
import { FairInputText } from "@/ui/primitives/FairInputText";
import { PRIVACY_POLICY_ROUTE, TERMS_OF_SERVICE_ROUTE } from "@/router/routes";

interface ErrorResponse {
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const isErrorResponse = (error: unknown): error is ErrorResponse => {
  return typeof error === "object" && error !== null && "response" in error;
};

const name = ref("");
const usernameError = ref("");

const email = ref("");
const emailError = ref("");

const password = ref("");
const passwordError = ref("");

const confirmPassword = ref("");
const confirmPasswordError = ref("");

const checked = ref(false);
const loadingState = ref(false);
const passwordVisible = ref(false);
const confirmVisible = ref(false);

const toast = useToast();
const { t } = useI18n();

const disableSubmit = computed(() => {
  return (
    email.value === "" ||
    password.value === "" ||
    confirmPassword.value === "" ||
    name.value === "" ||
    !!emailError.value.length ||
    !!passwordError.value.length ||
    !!confirmPasswordError.value.length ||
    !!usernameError.value.length ||
    !checked.value
  );
});

const sixSymbolsValidation = computed(() => password.value.length > 6);
const numberValidation = computed(() => /\d/.test(password.value));
const bigLetterValidation = computed(() => /\p{Lu}/u.test(password.value));
const smallLetterValidation = computed(() => /\p{Ll}/u.test(password.value));
const specialLetterValidation = computed(() =>
  /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
);

const passwordValidations = computed(() => [
  { label: t("password_validation.min_6_chars"), validation: sixSymbolsValidation.value },
  { label: t("password_validation.one_number"), validation: numberValidation.value },
  {
    label: t("password_validation.one_uppercase"),
    validation: bigLetterValidation.value,
  },
  {
    label: t("password_validation.one_lowercase"),
    validation: smallLetterValidation.value,
  },
  {
    label: t("password_validation.one_special_char"),
    validation: specialLetterValidation.value,
  },
]);

const validateUsername = () => {
  usernameError.value = name.value.length >= 3 ? "" : t("signup.username_error");
};

const validateEmail = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email.value)) {
    emailError.value = t("signup.email_error");
  } else {
    emailError.value = "";
  }
};

const validatePassword = () => {
  passwordError.value = password.value.length >= 8 ? "" : t("signup.password_error");
};

const validateConfirmPassword = () => {
  if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = t("signup.password_mismatch");
  } else {
    confirmPasswordError.value = "";
  }
};

const handleAccountCreate = async (event?: Event) => {
  if (event) {
    event.preventDefault();
  }

  loadingState.value = true;
  try {
    await useAuthStore().signUp({
      name: name.value,
      email: email.value,
      password: password.value,
    });

    toast.add({
      severity: "success",
      summary: t("signup.account_created"),
      detail: t("signup.email_verification"),
      life: 5000,
    });
  } catch (error: unknown) {
    console.error("Registration failed:", error);
    if (isErrorResponse(error) && error.response?.data?.detail) {
      emailError.value = t("signup.email_admin_confirm");
    } else {
      toast.add({
        severity: "error",
        summary: t("signup.registration_error"),
        detail: t("signup.registration_failed"),
        life: 5000,
      });
    }
  } finally {
    loadingState.value = false;
  }
};
</script>

<template>
  <form
    class="fair-auth-form"
    @submit.prevent="handleAccountCreate"
  >
    <div class="fair-auth-form__field">
      <FairInputText
        v-model="name"
        input-id="auth-signup-name"
        :placeholder="t('signup.username_label')"
        :aria-label="t('signup.username_label')"
        :invalid="!!usernameError"
        @input="validateUsername"
        @blur="validateUsername"
      />
      <p
        v-if="usernameError"
        class="fair-auth-form__error"
      >
        {{ usernameError }}
      </p>
    </div>

    <div class="fair-auth-form__field">
      <FairInputText
        v-model="email"
        input-id="auth-signup-email"
        :placeholder="t('signup.email_label')"
        :aria-label="t('signup.email_label')"
        :invalid="!!emailError"
        @input="validateEmail"
        @blur="validateEmail"
      />
      <p
        v-if="emailError"
        class="fair-auth-form__error"
      >
        {{ emailError }}
      </p>
    </div>

    <div class="fair-auth-form__field">
      <!-- The primitive hard-codes `type="text"`, so password fields ride in its
           slot: same shell and tokens, real `type="password"`. -->
      <FairInputText :invalid="!!passwordError">
        <input
          id="auth-signup-password"
          v-model="password"
          class="fair-input-text__control fair-text-input"
          :type="passwordVisible ? 'text' : 'password'"
          :placeholder="t('signup.password_label')"
          autocomplete="new-password"
          @input="validatePassword"
          @blur="validatePassword"
        >
        <button
          type="button"
          class="fair-auth-form__reveal"
          :aria-label="passwordVisible ? t('signup.hide_password') : t('signup.show_password')"
          @click="passwordVisible = !passwordVisible"
        >
          <FairIcon
            :name="passwordVisible ? 'hide' : 'show'"
            :size="20"
            color="currentColor"
          />
        </button>
      </FairInputText>
      <p
        v-if="passwordError"
        class="fair-auth-form__error"
      >
        {{ passwordError }}
      </p>
      <ul
        v-if="password"
        class="fair-auth-rules"
      >
        <li
          v-for="rule in passwordValidations"
          :key="rule.label"
          :class="['fair-auth-rules__item', rule.validation ? 'fair-auth-rules__item--met' : '']"
        >
          {{ rule.label }}
        </li>
      </ul>
    </div>

    <div class="fair-auth-form__field">
      <FairInputText :invalid="!!confirmPasswordError">
        <input
          id="auth-signup-confirm"
          v-model="confirmPassword"
          class="fair-input-text__control fair-text-input"
          :type="confirmVisible ? 'text' : 'password'"
          :placeholder="t('signup.confirm_password_label')"
          autocomplete="new-password"
          @input="validateConfirmPassword"
          @blur="validateConfirmPassword"
        >
        <button
          type="button"
          class="fair-auth-form__reveal"
          :aria-label="confirmVisible ? t('signup.hide_password') : t('signup.show_password')"
          @click="confirmVisible = !confirmVisible"
        >
          <FairIcon
            :name="confirmVisible ? 'hide' : 'show'"
            :size="20"
            color="currentColor"
          />
        </button>
      </FairInputText>
      <p
        v-if="confirmPasswordError"
        class="fair-auth-form__error"
      >
        {{ confirmPasswordError }}
      </p>
    </div>

    <div class="fair-auth-consent">
      <FairCheckbox
        v-model="checked"
        input-id="signup-policy"
        :show-label="false"
        :aria-label="t('signup.privacy_policy_prefix')"
      />
      <label
        class="fair-auth-consent__label"
        for="signup-policy"
      >
        {{ t("signup.privacy_policy_prefix") }}
        <RouterLink
          class="fair-auth-consent__link"
          :to="PRIVACY_POLICY_ROUTE"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
        >
          {{ t("signup.privacy_policy_link") }}
        </RouterLink>
        {{ t("signup.privacy_policy_and") }}
        <RouterLink
          class="fair-auth-consent__link"
          :to="TERMS_OF_SERVICE_ROUTE"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
        >
          {{ t("signup.terms_of_service_link") }}
        </RouterLink>
      </label>
    </div>

    <FairButton
      class="fair-auth-form__submit"
      type="submit"
      variant="primary"
      size="m"
      :rounded="false"
      :label="t('signup.create_account_button')"
      :disabled="disableSubmit || loadingState"
    />
  </form>
</template>
